import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LiveChat } from './components/LiveChat';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { ScrollToTopButton } from './components/ScrollToTopButton';
import { PageTransition } from './components/PageTransition';
// Home is the landing route — keep it eager so the first paint is instant.
import { Home } from './pages/Home';
import { CartProvider } from './components/CartContext';
import { CartDrawer } from './components/CartDrawer';
import { AnnouncementBar } from './components/AnnouncementBar';
import { I18nProvider } from './utils/i18n';
// Every other route is code-split and fetched on demand, so they no longer
// weigh down the initial bundle. Named exports are mapped to a default for lazy.
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Product = lazy(() => import('./pages/Product').then(m => ({
  default: m.Product
})));
const Project = lazy(() => import('./pages/Project').then(m => ({
  default: m.Project
})));
const HelpMeChoose = lazy(() => import('./pages/HelpMeChoose').then(m => ({
  default: m.HelpMeChoose
})));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({
  default: m.AboutPage
})));
const DeliveryZones = lazy(() => import('./pages/DeliveryZones').then(m => ({
  default: m.DeliveryZones
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const DeliveryPrep = lazy(() => import('./pages/DeliveryPrep').then(m => ({
  default: m.DeliveryPrep
})));
const CompareMaterials = lazy(() => import('./pages/CompareMaterials').then(m => ({
  default: m.CompareMaterials
})));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({
  default: m.NotFound
})));
const TrackOrder = lazy(() => import('./pages/TrackOrder').then(m => ({
  default: m.TrackOrder
})));
const TrackOrderLookup = lazy(() => import('./pages/TrackOrderLookup').then(m => ({
  default: m.TrackOrderLookup
})));
const Calculator = lazy(() => import('./pages/Calculator').then(m => ({
  default: m.Calculator
})));
const LocalVsNational = lazy(() => import('./pages/LocalVsNational').then(m => ({
  default: m.LocalVsNational
})));
const CityMaterial = lazy(() => import('./pages/CityMaterial').then(m => ({
  default: m.CityMaterial
})));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas').then(m => ({
  default: m.ServiceAreas
})));
const LogoConcepts = lazy(() => import('./components/LogoConcepts').then(m => ({
  default: m.LogoConcepts
})));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy').then(m => ({
  default: m.RefundPolicy
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({
  default: m.TermsOfService
})));
const DailyTicketForm = lazy(() => import('./pages/DailyTicketForm').then(m => ({
  default: m.DailyTicketForm
})));
function RouteFallback() {
  return <div className="min-h-[60vh] flex items-center justify-center bg-[#fdfbf7] dark:bg-[#222222]" role="status" aria-label="Loading" data-id="element-0" data-mp="13cc0oaivhkyl">
      <span className="h-8 w-8 rounded-full border-2 border-brand-orange border-t-transparent animate-spin" data-id="element-1" data-mp="1w3lwmelt75yl" />
    </div>;
}
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Two-pass scroll: first attempt accounts for the sticky navbar height,
      // then a follow-up corrects for late-rendering content (Services, etc.)
      // that can push the target further down the page.
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (!el) return;
        const navHeight = document.querySelector('nav')?.getBoundingClientRect().height || 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      };
      const t1 = setTimeout(scrollToTarget, 120);
      const t2 = setTimeout(scrollToTarget, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}
function AnimatedRoutes() {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-2" data-mp="vvgswgw9ncwe">
      <Suspense fallback={<RouteFallback data-id="element-4" data-mp="3knt2b2l0yxpz" />} data-id="element-3" data-mp="vyl07n512ora">
        <Routes location={location} key={location.pathname} data-id="element-5" data-mp="3kp42ksf9hncc">
          <Route path="/" element={<PageTransition data-id="element-7" data-mp="3gm7rrnvyqviy">
                <Home data-id="element-8" data-mp="3gkwduv8t07e4" />
              </PageTransition>} data-id="element-6" data-mp="3gmh80jr7hobg" />
          <Route path="/contact" element={<PageTransition data-id="element-10" data-mp="1w5q5iiva4bc2">
                <Contact data-id="element-11" data-mp="1w2c3xcufw65o" />
              </PageTransition>} data-id="element-9" data-mp="1w4oqk3b574p2" />
          <Route path="/shop/:slug" element={<PageTransition data-id="element-13" data-mp="206a1vk813ax5">
                <Product data-id="element-14" data-mp="255d8va7kjr7r" />
              </PageTransition>} data-id="element-12" data-mp="2071zt1p3lkql" />
          <Route path="/projects/:slug" element={<PageTransition data-id="element-16" data-mp="29vtmaube3cu4">
                <Project data-id="element-17" data-mp="29wva7zdi7tt9" />
              </PageTransition>} data-id="element-15" data-mp="253jx5gi3mgsw" />
          <Route path="/help" element={<PageTransition data-id="element-19" data-mp="2evg0b5z1kx3z">
                <HelpMeChoose data-id="element-20" data-mp="2euenavygu0jm" />
              </PageTransition>} data-id="element-18" data-mp="29xo5a333ouvk" />
          <Route path="/about" element={<PageTransition data-id="element-22" data-mp="2jsoiv7o3r35r">
                <AboutPage data-id="element-23" data-mp="2jw3zwg1c6m9t" />
              </PageTransition>} data-id="element-21" data-mp="2jthsqhicy8s4" />
          <Route path="/delivery-zones" element={<PageTransition data-id="element-25" data-mp="2nti5zytq0dxl">
                <DeliveryZones data-id="element-26" data-mp="2sufqwj647m2s" />
              </PageTransition>} data-id="element-24" data-mp="2nwnxtltqhtx0" />
          <Route path="/subcontractors" element={<Navigate to="/" replace data-id="element-28" data-mp="ti3gnj4mggid" />} data-id="element-27" data-mp="2sslczot9gu87" />
          <Route path="/contractor-ordering" element={<Navigate to="/" replace data-id="element-30" data-mp="ygojlqb9deai" />} data-id="element-29" data-mp="tj4rpnln1303" />
          <Route path="/diy-help" element={<Navigate to="/blog/perfect-gravel-driveway" replace data-id="element-32" data-mp="yir640d7cpah" />} data-id="element-31" data-mp="yhpw7z7xa8uc" />
          <Route path="/blog" element={<PageTransition data-id="element-34" data-mp="21tohdzj0mr68">
                <Blog data-id="element-35" data-mp="1wsr2pwx7sf9h" />
              </PageTransition>} data-id="element-33" data-mp="21sddlzpkljf4" />
          <Route path="/blog/:slug" element={<PageTransition data-id="element-37" data-mp="2as8wkqpdaipp">
                <BlogPost data-id="element-38" data-mp="2asikm8quqfsr" />
              </PageTransition>} data-id="element-36" data-mp="1wu28216b5v2p" />
          <Route path="/delivery-prep" element={<PageTransition data-id="element-40" data-mp="25sw7s0exr1kh">
                <DeliveryPrep data-id="element-41" data-mp="25rma3cbzvved" />
              </PageTransition>} data-id="element-39" data-mp="2apw5no1msy0i" />
          <Route path="/compare" element={<PageTransition data-id="element-43" data-mp="2kovdx5ihirmz">
                <CompareMaterials data-id="element-44" data-mp="2ko3jvz4jd1yt" />
              </PageTransition>} data-id="element-42" data-mp="2kpmyzmegh6z0" />
          <Route path="/calculator" element={<PageTransition data-id="element-46" data-mp="2fpiqaijshgjc">
                <Calculator data-id="element-47" data-mp="2tj003bmobew6" />
              </PageTransition>} data-id="element-45" data-mp="2fqtvmmsvuwck" />
          <Route path="/local-vs-national" element={<PageTransition data-id="element-49" data-mp="2pgwlie4pju6o">
                <LocalVsNational data-id="element-50" data-mp="2phointdydzz2" />
              </PageTransition>} data-id="element-48" data-mp="2tkkvf3gtb1v3" />
          <Route path="/track" element={<PageTransition data-id="element-52" data-mp="33hfrlfpm4q67">
                <TrackOrderLookup data-id="element-53" data-mp="33el6rn2ot3c9" />
              </PageTransition>} data-id="element-51" data-mp="33g4prlfq9shq" />
          <Route path="/track/:orderId" element={<PageTransition data-id="element-55" data-mp="3i3mxksol738d">
                <TrackOrder data-id="element-56" data-mp="3i3d773zmqu25" />
              </PageTransition>} data-id="element-54" data-mp="2yje9ax00guvs" />
          <Route path="/service-areas" element={<PageTransition data-id="element-58" data-mp="3n2zih7vwe08b">
                <ServiceAreas data-id="element-59" data-mp="3n27og1hy8ak5" />
              </PageTransition>} data-id="element-57" data-mp="3i1sbvc5hr738" />
          <Route path="/logo-concepts" element={<PageTransition data-id="element-61" data-mp="384xbenpnqwmb">
                <LogoConcepts data-id="element-62" data-mp="3d4jql2p1qmxr" />
              </PageTransition>} data-id="element-60" data-mp="385ysp9x9ofch" />
          <Route path="/refund-policy" element={<PageTransition data-id="element-64" data-mp="2zd57kvv52lqp">
                <RefundPolicy data-id="element-65" data-mp="2zcve2yavxwg9" />
              </PageTransition>} data-id="element-63" data-mp="3d2yuh8n32vxs" />
          <Route path="/privacy-policy" element={<PageTransition data-id="element-67" data-mp="3443n3t8azfsw">
                <PrivacyPolicy data-id="element-68" data-mp="345evk6cp1bqc" />
              </PageTransition>} data-id="element-66" data-mp="2zbajj8okmdie" />
          <Route path="/terms-of-service" element={<PageTransition data-id="element-70" data-mp="2pgsdhwvom5vf">
                <TermsOfService data-id="element-71" data-mp="2ue22jbf5sfiq" />
              </PageTransition>} data-id="element-69" data-mp="2pfqr4w97twye" />
          <Route path="/form" element={<PageTransition data-id="element-73" data-mp="2ge9e5mr3ubdx">
                <DailyTicketForm data-id="element-74" data-mp="2ge11nvyw9839" />
              </PageTransition>} data-id="element-72" data-mp="2uebsx0448ooy" />

          <Route path="/gravel-delivery/:citySlug/:materialSlug" element={<PageTransition data-id="element-76" data-mp="2kfcuead2e9fo">
                <CityMaterial data-id="element-77" data-mp="2khpbklb00fro" />
              </PageTransition>} data-id="element-75" data-mp="2kfuy1s223pxm" />
          <Route path="*" element={<PageTransition data-id="element-79" data-mp="30tau1pia95zv">
                <NotFound data-id="element-80" data-mp="2vwk7qkfvxe60" />
              </PageTransition>} data-id="element-78" data-mp="30uvegmavv8k0" />
        </Routes>
      </Suspense>
    </AnimatePresence>;
}
function AppContent({
  isDark,
  toggleDark
}: {
  isDark: boolean;
  toggleDark: () => void;
}) {
  const location = useLocation();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  // The daily ticket form (/form) is a standalone operational document. It
  // renders bare — no site chrome (navbar, footer, announcement bar, live
  // chat, sticky CTA, cart) — so it prints clean and stays out of the way.
  const isStandalonePage = location.pathname === '/form';
  if (isStandalonePage) {
    // The daily ticket is a printed white document — never apply the `dark`
    // class here so it always renders on a clean white surface regardless of
    // the visitor's theme.
    return <div className="min-h-screen font-sans" data-id="element-81" data-mp="1u91i7ibnjors">
        <ScrollToHash data-id="element-82" data-mp="9pny0oz4efwg" />
        <AnimatedRoutes data-id="element-83" data-mp="9ocwkvt5dk8i" />
      </div>;
  }
  return <div className={`min-h-screen flex flex-col font-sans ${isDark ? 'dark' : ''} ${isContractorPage ? 'contractor-theme' : ''}`} data-id="element-84" data-mp="dqhn3loau0qx">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-brand-orange focus:text-white focus:rounded-lg focus:font-bold focus:outline-none focus:ring-2 focus:ring-white" data-id="element-85" data-mp="ipazfarunlpi">
        Skip to main content
      </a>
      <div className="flex-grow bg-[#fdfbf7] dark:bg-[#222222] transition-colors duration-300 pb-16 md:pb-0" id="main-content" data-id="element-86" data-mp="3r6fmfx70gj1d">
        <ScrollToHash data-id="element-87" data-mp="3w362wlmq9gnb" />
        <AnnouncementBar data-id="element-88" data-mp="4pt8opuztydi" />
        <Navbar isDark={isDark} toggleDark={toggleDark} data-id="element-89" data-mp="4qbas34c76tc" />
        <AnimatedRoutes data-id="element-90" data-mp="4rw63uyh6ts9" />
        <Footer data-id="element-91" data-mp="33g34dti1x5x2" />
        <LiveChat data-id="element-92" data-mp="33gl9zgqlswhn" />
        <StickyMobileCTA data-id="element-93" data-mp="33f0hrxrrhpmy" />
        <ScrollToTopButton data-id="element-94" data-mp="33dpedz28ajwd" />
        <CartDrawer data-id="element-95" data-mp="38emtg2rxyxtn" />
      </div>
    </div>;
}
// Decide the initial theme for a first-time visitor (no saved preference yet):
//   1. An explicit saved choice always wins — a returning visitor who picked a
//      theme keeps it.
//   2. Everyone else starts in LIGHT mode.
//
// Light is the deliberate default. Customers are contractors and homeowners
// pricing material outdoors on a phone in daylight, so light mode is the
// higher-value surface and the one held to the tightest design standard. The
// previous behavior deferred to the OS `prefers-color-scheme` and then to a
// time-of-day clock rule, which meant a majority of first-time visitors landed
// in dark mode by accident — and made the very first impression the mode that
// gets less design attention. The toggle still switches modes and still
// persists, so nothing is taken away from anyone who prefers dark.
function getInitialTheme(): boolean {
  try {
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
  } catch {
    // localStorage can throw in private mode / embedded webviews / when
    // storage is blocked. Fall through to the light default.
  }
  return false;
}
export function App() {
  const [isDark, setIsDark] = useState(getInitialTheme);
  useEffect(() => {
    try {
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    } catch {}
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);
  const toggleDark = () => setIsDark(!isDark);
  return <I18nProvider data-id="element-96" data-mp="389eedryjnvwc">
      <Router data-id="element-97" data-mp="388v858i9avy5">
        <CartProvider data-id="element-98" data-mp="3894py8t5dwsr">
          <AppContent isDark={isDark} toggleDark={toggleDark} data-id="element-99" data-mp="387tj204eo8xf" />
        </CartProvider>
      </Router>
    </I18nProvider>;
}